import type { WidgetAttributes as GQLInput } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * `WidgetAttributes`, made `Partial`.
 *
 * `description` and `loaderUrl` are non-optional on the wire, and neither widget form can satisfy
 * that at the type level: the create page calls `useForm()` with no generic, so its values are
 * vee-validate's `GenericObject`, and the edit page infers its `TValues` from a widget read back
 * from the server, whose fields are all optional. The pages used to hide both with a hand-rolled
 * `take<WidgetAttributes>(values, …)`, whose cast this module replaces. The required fields stay
 * the server's and the form's to enforce — `toGqlInput` still returns `GQLInput`.
 */
export type WidgetConfigInput = Partial<GQLInput>;

/**
 * Every `WidgetAttributes` field, enumerated from the schema. A misspelling here is a compile
 * error; an omission is not, which is what `WidgetConfigInput.spec.ts`'s round-trip test guards.
 */
const GQL_FIELDS = ['configuration', 'description', 'id', 'loaderUrl', 'style'] as const;

/**
 * Compile-time exhaustiveness guard. `take()` errors on a misspelled key but not on a *missing*
 * one, so without this a field added to the input by a later codegen run would silently stop
 * persisting. `keyof Partial<T>` is `keyof T`, so guarding the wire type covers both.
 */
type _AllFieldsListed<T, Listed extends PropertyKey> =
  [Exclude<keyof T, Listed>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllFieldsListed<GQLInput, (typeof GQL_FIELDS)[number]> = true;
void _allGqlFieldsListed;

/**
 * Whitelists the form's values down to what the mutation accepts. Both widget pages hand over a
 * form seeded from a widget the server returned, so the read-only `embedUrl` rides along on the
 * create *and* the update path — vee-validate keeps initial-value keys that have no registered
 * field — and the mutation rejects unknown arguments outright.
 *
 * `take()` drops undefined keys, so the duplicate flow's deliberate `id: undefined` still creates
 * rather than updates.
 */
export const toGqlInput = (input: WidgetConfigInput): GQLInput =>
  take<GQLInput, GQLInput>(input, ...GQL_FIELDS);
